import { BandSeries, SizeOption, IVariant, ColorOption, BandMaker } from '../types';
// Narrow import, not the `./util` barrel: the barrel pulls in money -> errors -> analytics -> axios,
// which Jest cannot parse, and that would make this module untestable in isolation.
import { expectUnreachable } from './util/expectUnreachable';
import i18next from '../../i18n';
import { FiCountry } from '../contexts/LocaleContext';

export type BandWidth = '3/4"' | '1"';
export type BandWidthCm = '2 cm' | '2.5 cm';

export type MeasurementUnit = 'in' | 'cm';

export function getDefaultMeasurementUnit(country: FiCountry): MeasurementUnit {
  return country === FiCountry.US ? 'in' : 'cm';
}

export type WeightUnit = 'lbs' | 'kg';

export function getDefaultWeightUnit(country: FiCountry): WeightUnit {
  switch (country) {
    case FiCountry.FR:
    case FiCountry.DE:
    case FiCountry.ES:
    case FiCountry.FI:
    case FiCountry.MT:
    case FiCountry.RO:
    case FiCountry.AT:
      return 'kg';
    default:
      return 'lbs';
  }
}

export function lbsToKg(lbs: number): number {
  return Math.round(lbs * 0.453592);
}

export function kgToLbs(kg: number): number {
  return Math.round(kg * 2.20462);
}

export function weightLimits(unit: WeightUnit): { min: number; max: number } {
  return unit === 'kg' ? { min: 2, max: 91 } : { min: 5, max: 200 };
}

// Only show the size options that are available for the F1 band series based on inventory.
export const f1BandSizeOptions = [SizeOption.Small, SizeOption.Medium, SizeOption.Large, SizeOption.XLarge] as const;
export type F1BandSizeOption = (typeof f1BandSizeOptions)[number];

export const f3BandSizeOptions = [
  SizeOption.XSmall,
  SizeOption.Small,
  SizeOption.Medium,
  SizeOption.Large,
  SizeOption.XLarge,
] as const;
export type F3BandSizeOption = (typeof f3BandSizeOptions)[number];

export interface SizeOptionProps {
  bandSeries: BandSeries;
  sizeOption: F1BandSizeOption | F3BandSizeOption;
  bandMaker?: BandMaker;
  color?: ColorOption;
  measurementUnit?: MeasurementUnit;
  weightUnit?: WeightUnit;
}

export interface BandSizeDetails {
  dogList: string;
  sizeName: string;
  neckSize: string;
  bandWidth: BandWidth | BandWidthCm;
  /** Only set for Fi F3 bands — makers' bands have their own neck ranges and no weight guidance. */
  weightRange?: string;
  /** Two representative breeds, for the size card. `dogList` is the longer list used in the guide. */
  dogExamples?: string;
}

/**
 * Weight bands for Fi F3 collars, derived from what owners at each weight actually keep
 * (64,565 Series 3+ purchases, Aug 2025 – Jul 2026) rather than from breed averages.
 * Buyers whose size matches their band exchange at 0.67%; one size off is 3.86%.
 *
 * The M/L line at 40 lb is close either way (M 40/2,503 vs L 17/1,464). It sits at 40 so the
 * guidance never points a buyer away from Large, which is the lowest-exchange size we sell.
 */
export function f3WeightRangeForSize(sizeOption: F3BandSizeOption, weightUnit: WeightUnit): string {
  const metric = weightUnit === 'kg';

  if (sizeOption === SizeOption.XSmall) {
    return metric ? i18next.t('weight_range_x_small_kg') : i18next.t('weight_range_x_small_lb');
  } else if (sizeOption === SizeOption.Small) {
    return metric ? i18next.t('weight_range_small_kg') : i18next.t('weight_range_small_lb');
  } else if (sizeOption === SizeOption.Medium) {
    return metric ? i18next.t('weight_range_medium_kg') : i18next.t('weight_range_medium_lb');
  } else if (sizeOption === SizeOption.Large) {
    return metric ? i18next.t('weight_range_large_kg') : i18next.t('weight_range_large_lb');
  } else if (sizeOption === SizeOption.XLarge) {
    return metric ? i18next.t('weight_range_x_large_kg') : i18next.t('weight_range_x_large_lb');
  } else {
    expectUnreachable(sizeOption);
    throw new Error(i18next.t('unknown_size_option', { sizeOption }));
  }
}

const f3SizeOrder: F3BandSizeOption[] = [
  SizeOption.XSmall,
  SizeOption.Small,
  SizeOption.Medium,
  SizeOption.Large,
  SizeOption.XLarge,
];

/**
 * Breeds carrying a neck as wide as the head, where the band has to clear the skull rather than
 * just the neck. Weight alone puts these dogs a band too small.
 *
 * Matched as patterns rather than exact names so variants and crosses land here the same way
 * "American French Bulldog" already did — `/bulldog/i` covers the French, English, American and
 * Olde English lines in one rule.
 */
const wideNeckBreedPatterns = [/bulldog/i, /\bpug\b/i, /boston terrier/i, /\bboxer\b/i];

/** Whether a breed name is one whose neck is as wide as its head. */
export function isWideNeckBreed(breedName?: string): boolean {
  if (!breedName) return false;
  return wideNeckBreedPatterns.some((pattern) => pattern.test(breedName));
}

/**
 * The smallest size the weight bands support for a dog, used as a floor on the sizing guide's
 * recommendation.
 *
 * The guide's distributions are built from what shoppers *bought*, so they carry the sizing
 * mistakes we are trying to stop: at 50 lb they lean Medium 55% / Large 42%, while owners who
 * bought Medium at that weight exchanged 3.80% against 0.51% for Large. Without this floor the
 * guide would recommend Medium to a dog whose size card says Large.
 *
 * Wide-necked breeds shift a band down on top of that, because the band has to clear the skull.
 */
export function f3MinimumSizeForWeight(weightLbs: number, breedName?: string): F3BandSizeOption {
  if (isWideNeckBreed(breedName)) {
    if (weightLbs < 15) return SizeOption.Small;
    if (weightLbs < 30) return SizeOption.Medium;
    // The XLarge tier matters here: without it the wide-neck branch caps at Large and a 110 lb
    // Boxer would size *down* on joining the set.
    return weightLbs < 95 ? SizeOption.Large : SizeOption.XLarge;
  }

  if (weightLbs < 10) return SizeOption.XSmall;
  if (weightLbs < 20) return SizeOption.Small;
  if (weightLbs < 40) return SizeOption.Medium;
  return weightLbs < 95 ? SizeOption.Large : SizeOption.XLarge;
}

/** The larger of two F3 sizes, so a floor can be applied without caring which side is bigger. */
export function f3LargerSize(a: F3BandSizeOption, b: F3BandSizeOption): F3BandSizeOption {
  return f3SizeOrder.indexOf(a) >= f3SizeOrder.indexOf(b) ? a : b;
}

function dogExamplesForSize(sizeOption: SizeOption): string {
  if (sizeOption === SizeOption.XSmall) {
    return i18next.t('dog_examples_x_small');
  } else if (sizeOption === SizeOption.Small) {
    return i18next.t('dog_examples_small');
  } else if (sizeOption === SizeOption.Medium) {
    return i18next.t('dog_examples_medium');
  } else if (sizeOption === SizeOption.Large) {
    return i18next.t('dog_examples_large');
  } else if (sizeOption === SizeOption.XLarge) {
    return i18next.t('dog_examples_x_large');
  } else {
    expectUnreachable(sizeOption);
    throw new Error(i18next.t('unknown_size_option', { sizeOption }));
  }
}

export function sortedSizeOptionsForVariants(variants: IVariant[]) {
  const sizeOptions = new Set(variants.map((variant) => variant.options.size));
  const sizeSortOrder = (sizeOption: SizeOption) => {
    if (sizeOption === SizeOption.XSmall) {
      return 0;
    } else if (sizeOption === SizeOption.Small) {
      return 1;
    } else if (sizeOption === SizeOption.Medium) {
      return 2;
    } else if (sizeOption === SizeOption.Large) {
      return 3;
    } else if (sizeOption === SizeOption.XLarge) {
      return 4;
    } else {
      expectUnreachable(sizeOption);
      throw new Error(i18next.t('unknown_size_option', { sizeOption }));
    }
  };

  return Array.from(sizeOptions).sort((a, b) => sizeSortOrder(a) - sizeSortOrder(b));
}

export function commonDetailsForSize(sizeOption: SizeOption): {
  dogList: string;
  sizeName: string;
} {
  if (sizeOption === SizeOption.XSmall) {
    return {
      dogList: i18next.t('dog_list_x_small'),
      sizeName: i18next.t('size_name_x_small'),
    };
  } else if (sizeOption === SizeOption.Small) {
    return {
      dogList: i18next.t('dog_list_small'),
      sizeName: i18next.t('size_name_small'),
    };
  } else if (sizeOption === SizeOption.Medium) {
    return {
      dogList: i18next.t('dog_list_medium'),
      sizeName: i18next.t('size_name_medium'),
    };
  } else if (sizeOption === SizeOption.Large) {
    return {
      dogList: i18next.t('dog_list_large'),
      sizeName: i18next.t('size_name_large'),
    };
  } else if (sizeOption === SizeOption.XLarge) {
    return {
      dogList: i18next.t('dog_list_x_large'),
      sizeName: i18next.t('size_name_x_large'),
    };
  } else {
    expectUnreachable(sizeOption);
    throw new Error(i18next.t('unknown_size_option', { sizeOption }));
  }
}

function sizeDetailsForF1SizeOption(sizeOption: F1BandSizeOption): BandSizeDetails {
  const commonDetails = commonDetailsForSize(sizeOption);

  if (sizeOption === SizeOption.Small) {
    return {
      ...commonDetails,
      neckSize: '11.5" - 13.5"',
      bandWidth: '1"',
    };
  } else if (sizeOption === SizeOption.Medium) {
    return {
      ...commonDetails,
      neckSize: '13" - 16.5"',
      bandWidth: '1"',
    };
  } else if (sizeOption === SizeOption.Large) {
    return {
      ...commonDetails,
      neckSize: '16" - 22.5"',
      bandWidth: '1"',
    };
  } else if (sizeOption === SizeOption.XLarge) {
    return {
      ...commonDetails,
      neckSize: '22" - 34.5"',
      bandWidth: '1"',
    };
  } else {
    expectUnreachable(sizeOption);
    throw new Error(i18next.t('unknown_size_option', { sizeOption }));
  }
}

function sizeDetailsForF3SizeOption(sizeOption: F3BandSizeOption): BandSizeDetails {
  const commonDetails = commonDetailsForSize(sizeOption);

  if (sizeOption === SizeOption.XSmall) {
    return {
      ...commonDetails,
      neckSize: '9.5" - 11"',
      bandWidth: '3/4"',
    };
  } else if (sizeOption === SizeOption.Small) {
    return {
      ...commonDetails,
      neckSize: '10.5" - 13"',
      bandWidth: '3/4"',
    };
  } else if (sizeOption === SizeOption.Medium) {
    return {
      ...commonDetails,
      neckSize: '12.5" - 16.5"',
      bandWidth: '1"',
    };
  } else if (sizeOption === SizeOption.Large) {
    return {
      ...commonDetails,
      neckSize: '15.5" - 22"',
      bandWidth: '1"',
    };
  } else if (sizeOption === SizeOption.XLarge) {
    return {
      ...commonDetails,
      neckSize: '20" - 31"',
      bandWidth: '1"',
    };
  } else {
    expectUnreachable(sizeOption);
    throw new Error(i18next.t('unknown_size_option', { sizeOption }));
  }
}

function sizeDetailsForF3SizeOptionCm(sizeOption: F3BandSizeOption): BandSizeDetails {
  const commonDetails = commonDetailsForSize(sizeOption);

  if (sizeOption === SizeOption.XSmall) {
    return {
      ...commonDetails,
      neckSize: '24 - 28 cm',
      bandWidth: '2 cm',
    };
  } else if (sizeOption === SizeOption.Small) {
    return {
      ...commonDetails,
      neckSize: '27 - 33 cm',
      bandWidth: '2 cm',
    };
  } else if (sizeOption === SizeOption.Medium) {
    return {
      ...commonDetails,
      neckSize: '32 - 42 cm',
      bandWidth: '2.5 cm',
    };
  } else if (sizeOption === SizeOption.Large) {
    return {
      ...commonDetails,
      neckSize: '39 - 56 cm',
      bandWidth: '2.5 cm',
    };
  } else if (sizeOption === SizeOption.XLarge) {
    return {
      ...commonDetails,
      neckSize: '51 - 79 cm',
      bandWidth: '2.5 cm',
    };
  } else {
    expectUnreachable(sizeOption);
    throw new Error(i18next.t('unknown_size_option', { sizeOption }));
  }
}

function sizeDetailsForMakersBands(sizeOption: SizeOption, colorOption: ColorOption): BandSizeDetails | null {
  const commonDetails = commonDetailsForSize(sizeOption);

  if (colorOption === ColorOption.ZeeDogPhantom) {
    if (sizeOption === SizeOption.Small) {
      return {
        ...commonDetails,
        neckSize: '10.5" - 13"',
        bandWidth: '3/4"',
      };
    } else if (sizeOption === SizeOption.Medium) {
      return {
        ...commonDetails,
        neckSize: '13" - 16.5"',
        bandWidth: '1"',
      };
    } else if (sizeOption === SizeOption.Large) {
      return {
        ...commonDetails,
        neckSize: '16" - 22"',
        bandWidth: '1"',
      };
    } else if (sizeOption === SizeOption.XLarge) {
      return {
        ...commonDetails,
        neckSize: '20" - 31"',
        bandWidth: '1"',
      };
    }
  } else if (colorOption === ColorOption.StuntPuppyGoDog || colorOption === ColorOption.StuntPuppyTealBiothane) {
    if (sizeOption === SizeOption.Small) {
      return {
        ...commonDetails,
        neckSize: '10.75" - 12.5"',
        bandWidth: '3/4"',
      };
    } else if (sizeOption === SizeOption.Medium) {
      return {
        ...commonDetails,
        neckSize: '12.5" - 15.5"',
        bandWidth: '1"',
      };
    } else if (sizeOption === SizeOption.Large) {
      return {
        ...commonDetails,
        neckSize: '15.5" - 20.5"',
        bandWidth: '1"',
      };
    } else if (sizeOption === SizeOption.XLarge) {
      return {
        ...commonDetails,
        neckSize: '19.5" - 30"',
        bandWidth: '1"',
      };
    }
  } else if (
    colorOption === ColorOption.StuntPuppyPinesBlue ||
    colorOption === ColorOption.StuntPuppyFishtailTeal ||
    colorOption === ColorOption.StuntPuppyFloraFrolic
  ) {
    if (sizeOption === SizeOption.Small) {
      return {
        ...commonDetails,
        neckSize: '10.75" - 12.5"',
        bandWidth: '3/4"',
      };
    } else if (sizeOption === SizeOption.Medium) {
      return {
        ...commonDetails,
        neckSize: '12.5" - 15.5"',
        bandWidth: '1"',
      };
    } else if (sizeOption === SizeOption.Large) {
      return {
        ...commonDetails,
        neckSize: '15.5" - 20.5"',
        bandWidth: '1"',
      };
    } else if (sizeOption === SizeOption.XLarge) {
      return {
        ...commonDetails,
        neckSize: '19.5" - 30"',
        bandWidth: '1"',
      };
    }
  } else if (colorOption === ColorOption.MimiGreenRedBiothane) {
    if (sizeOption === SizeOption.Small) {
      return {
        ...commonDetails,
        neckSize: '12" - 13"',
        bandWidth: '3/4"',
      };
    } else if (sizeOption === SizeOption.Medium) {
      return {
        ...commonDetails,
        neckSize: '13" - 15"',
        bandWidth: '1"',
      };
    } else if (sizeOption === SizeOption.Large) {
      return {
        ...commonDetails,
        neckSize: '16" - 22"',
        bandWidth: '1"',
      };
    } else if (sizeOption === SizeOption.XLarge) {
      return {
        ...commonDetails,
        neckSize: '22" - 26"',
        bandWidth: '1"',
      };
    }
  } else if (colorOption === ColorOption.MimiGreenPinkReflective) {
    if (sizeOption === SizeOption.Small) {
      return {
        ...commonDetails,
        neckSize: '12" - 13"',
        bandWidth: '3/4"',
      };
    } else if (sizeOption === SizeOption.Medium) {
      return {
        ...commonDetails,
        neckSize: '13" - 15"',
        bandWidth: '1"',
      };
    } else if (sizeOption === SizeOption.Large) {
      return {
        ...commonDetails,
        neckSize: '16" - 22"',
        bandWidth: '1"',
      };
    } else if (sizeOption === SizeOption.XLarge) {
      return {
        ...commonDetails,
        neckSize: '22" - 26"',
        bandWidth: '1"',
      };
    }
  } else if (colorOption === ColorOption.RopeHoundsMountainTop || colorOption === ColorOption.RopeHoundsBubblegumPop) {
    if (sizeOption === SizeOption.Small) {
      return {
        ...commonDetails,
        neckSize: '12" - 15"',
        bandWidth: '1"',
      };
    } else if (sizeOption === SizeOption.Medium) {
      return {
        ...commonDetails,
        neckSize: '15" - 20"',
        bandWidth: '1"',
      };
    } else if (sizeOption === SizeOption.Large) {
      return {
        ...commonDetails,
        neckSize: '17" - 24"',
        bandWidth: '1"',
      };
    }
  } else if (
    colorOption === ColorOption.LandsharkSupplyBlackBlue ||
    colorOption === ColorOption.LandsharkSupplyBlackRed
  ) {
    if (sizeOption === SizeOption.Small) {
      return {
        ...commonDetails,
        neckSize: '13" - 16"',
        bandWidth: '1"',
      };
    } else if (sizeOption === SizeOption.Medium) {
      return {
        ...commonDetails,
        neckSize: '15" - 19"',
        bandWidth: '1"',
      };
    } else if (sizeOption === SizeOption.Large) {
      return {
        ...commonDetails,
        neckSize: '17" - 23"',
        bandWidth: '1"',
      };
    } else if (sizeOption === SizeOption.XLarge) {
      return {
        ...commonDetails,
        neckSize: '19" - 27"',
        bandWidth: '1"',
      };
    }
  }

  // For display purposes, fallback to default F3 sizing
  return null;
}

export function sizeDetailsForSizeOption(props: SizeOptionProps) {
  if (props.bandMaker !== BandMaker.Fi && props.color) {
    return sizeDetailsForMakersBands(props.sizeOption, props.color);
  }

  if (props.bandSeries === BandSeries.F1) {
    return sizeDetailsForF1SizeOption(props.sizeOption as F1BandSizeOption);
  } else if (props.bandSeries === BandSeries.F3) {
    const details =
      props.measurementUnit === 'cm'
        ? sizeDetailsForF3SizeOptionCm(props.sizeOption)
        : sizeDetailsForF3SizeOption(props.sizeOption);

    return {
      ...details,
      weightRange: f3WeightRangeForSize(props.sizeOption, props.weightUnit ?? 'lbs'),
      dogExamples: dogExamplesForSize(props.sizeOption),
    };
  } else {
    expectUnreachable(props.bandSeries);
    throw new Error(i18next.t('unknown_band_series', { bandSeries: props.bandSeries }));
  }
}
